/**
 * Consent Timeline View Component
 *
 * Displays chronological timeline of consent journey events using Equal DS Timeline.
 * Webhook events get an inline "View Details" button that opens a modal.
 */

import React, { useState, useMemo } from 'react';
import { Timeline } from 'equal-ds-ui';
import { Clock } from 'lucide-react';
import type { ConsentTimeline, TimelineEvent } from '../../../types/timeline';
import { WebhookDetailModal } from './WebhookDetailModal';

interface ConsentTimelineViewProps {
  timeline: ConsentTimeline | null;
}

export const ConsentTimelineView: React.FC<ConsentTimelineViewProps> = ({ timeline }) => {
  const [selectedWebhook, setSelectedWebhook] = useState<TimelineEvent | null>(null);

  // Inject "View Details" action into webhook events so it appears inline in the timeline row
  const eventsWithActions = useMemo(() => {
    if (!timeline) return [];
    return timeline.events.map(event => {
      const isWebhook = (event.rawData?.event_type as string)?.startsWith('WEBHOOK_');
      if (!isWebhook) return event;

      return {
        ...event,
        action: {
          type: 'button' as const,
          label: 'View Details',
          onClick: () => setSelectedWebhook(event),
        },
      };
    });
  }, [timeline]);

  if (!timeline || timeline.events.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-text-tertiary">
        <p>No consent timeline events available.</p>
      </div>
    );
  }

  const { overallTAT } = timeline;
  const useFlexHeight = eventsWithActions.length >= 4;

  return (
    <div className={`flex flex-col space-y-3 ${useFlexHeight ? 'h-full' : ''}`}>
      {/* Timeline Header with TAT */}
      <div className="flex items-center justify-between mb-1.5 flex-shrink-0">
        <h3 className="text-sm font-medium text-text-primary tracking-wider">CONSENT TIMELINE</h3>
        {overallTAT?.duration && (
          <div className="flex items-center gap-1">
            <span className="text-sm font-medium text-text-muted tracking-wider">OVERALL TAT:</span>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-text-primary" />
              <span className="text-sm font-medium text-text-primary tracking-wide">
                {overallTAT.duration}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className={`bg-background-secondary border border-border-light rounded-xl p-4 ${
        useFlexHeight ? 'flex-1 min-h-0 overflow-y-auto' : ''
      }`}>
        <Timeline
          items={eventsWithActions as any}
          variant="default"
          showConnectors={true}
          connectorGap={2}
          className={useFlexHeight ? '!justify-start gap-6' : '!justify-start !h-auto gap-6'}
        />
      </div>

      {/* Modal */}
      {selectedWebhook && (
        <WebhookDetailModal event={selectedWebhook} onClose={() => setSelectedWebhook(null)} />
      )}
    </div>
  );
};
